import React, { useMemo, useState } from "react";
import { PageHeader, Field, NumberInput, Result, FormulaBox, Note } from "@/components/CalcUI";
import { fmt } from "@/lib/utils";
import { useT } from "@/i18n/LanguageProvider";

export default function DripRate() {
  const t = useT();
  const DROP_FACTORS = [
    { labelKey: "drip.factor.macro10", value: 10 },
    { labelKey: "drip.factor.macro15", value: 15 },
    { labelKey: "drip.factor.macro20", value: 20 },
    { labelKey: "drip.factor.micro60", value: 60 },
  ];

  const [volume, setVolume] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [dropFactor, setDropFactor] = useState(20);

  const totalMinutes = (parseFloat(hours) || 0) * 60 + (parseFloat(minutes) || 0);
  const vol = parseFloat(volume);

  const result = useMemo(() => {
    if (!vol || vol <= 0 || totalMinutes <= 0 || !dropFactor) return null;
    const mlPerHr = (vol / totalMinutes) * 60;
    const gttPerMin = (vol * dropFactor) / totalMinutes;
    return { mlPerHr, gttPerMin };
  }, [vol, totalMinutes, dropFactor]);

  return (
    <div data-testid="page-drip-rate">
      <PageHeader title={t("drip.title")} subtitle={t("drip.subtitle")} formula={t("drip.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
        <div className="space-y-6">
          <Field label={t("drip.volume")} unit="ml" testid="volume">
            <NumberInput
              value={volume}
              onChange={setVolume}
              placeholder={t("drip.volume.placeholder")}
              min={0}
              testid="volume-input"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("drip.hours")} unit="h" testid="hours">
              <NumberInput
                value={hours}
                onChange={setHours}
                placeholder={t("drip.hours.placeholder")}
                min={0}
                testid="hours-input"
              />
            </Field>
            <Field label={t("drip.minutes")} unit="min" testid="minutes">
              <NumberInput
                value={minutes}
                onChange={setMinutes}
                placeholder={t("drip.minutes.placeholder")}
                min={0}
                testid="minutes-input"
              />
            </Field>
          </div>

          <Field label={t("drip.factor")} testid="drop-factor">
            <select
              value={dropFactor}
              onChange={(e) => setDropFactor(Number(e.target.value))}
              data-testid="drop-factor-select"
              className="w-full bg-white border border-stone-200 rounded-sm px-3 py-2.5 text-base font-sans hover:border-stone-300 focus:border-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-700/30 transition-colors duration-100"
            >
              {DROP_FACTORS.map((d) => (
                <option key={d.value} value={d.value}>
                  {t(d.labelKey)}
                </option>
              ))}
            </select>
          </Field>

          <Note>{t("drip.note")}</Note>
        </div>

        <div className="space-y-4">
          <Result
            label={t("drip.result.gtt")}
            value={result ? fmt(result.gttPerMin, 0) : "—"}
            unit="gtt / min"
            big
            testid="result-gtt-min"
          />
          <Result
            label={t("drip.result.ml")}
            value={result ? fmt(result.mlPerHr, 1) : "—"}
            unit="ml / hr"
            testid="result-ml-hr"
          />
          <FormulaBox testid="breakdown-box">
            {result
              ? `(${fmt(vol, 0)} ml × ${dropFactor} gtt/ml) ÷ ${totalMinutes} min = ${fmt(
                  result.gttPerMin,
                  2
                )} gtt/min\n${fmt(vol, 0)} ml ÷ (${totalMinutes}/60 hr) = ${fmt(result.mlPerHr, 2)} ml/hr`
              : t("drip.breakdown.empty")}
          </FormulaBox>
        </div>
      </div>
    </div>
  );
}
